import { addDays, format, subMonths } from 'date-fns';

import { PRESET_DATES_HELPERS } from 'lib/dateUtilities';
import { createReducer } from 'lib/utilities.js';

const snakeToCamel = (s) => s.replace(/(_\w)/g, (k) => k[1].toUpperCase());

const mapToCamelCase = (start) => {
  return Object.fromEntries(Object.entries(start).map(([key, value]) => [String(key).includes('_') ? snakeToCamel(key) : key, value]));
};

const formatInstagramInsights = ({ payload }) => {
  const { reach_28_day, impressions_28_day, data_date } = payload;
  const date = format(addDays(new Date(data_date), 3), 'MMM');
  const instagramReach = reach_28_day ? { name: date, Reach: reach_28_day } : null;
  const instagramImpressions = impressions_28_day ? { name: date, Impressions: impressions_28_day } : null;
  return { instagramReach, instagramImpressions };
};

const dates = {
  startDate: format(new Date(PRESET_DATES_HELPERS.startOfLastMonth), 'yyyy-MM-dd'),
  endDate: format(new Date(PRESET_DATES_HELPERS.endOfLastMonth), 'yyyy-MM-dd'),
  prevStart: format(new Date(PRESET_DATES_HELPERS.startOfMonthBeforeLast), 'yyyy-MM-dd'),
  prevEnd: format(new Date(PRESET_DATES_HELPERS.endOfMonthBeforeLast), 'yyyy-MM-dd'),
};

const initialState = {
  triggerFetch: true,
  crmDates: dates,
  // Instagram data
  instagramFollowerCount: undefined,
  //Facebook load states
  facebookPageImpressionsLoading: false,
  facebookPageVideoViewsLoading: false,
  facebookPageSourceDataLoading: false,
  facebookPagePaidNonPaidDataLoading: false,
  facebookDemographicsDataLoading: false,
  facebookLocationDataLoading: false,
  facebookPostsLoading: false,
  facebookViewSocialSourcesLoading: false,
  // Instagram load states
  instagramCurrentMonthLoading: false,
  instagramReachThreeMonthsAgoLoading: false,
  instagramGenderDataLoading: false,
  //Facebook data
  // Previous 3 months of data
  facebookPageLikeCount: undefined,
  facebookPageImpressions: undefined,
  facebookPageVideoViews: undefined,
  facebookPageEngagement: undefined,
  facebookPageClicks: undefined,
  facebookPageSourceData: undefined,
  facebookPagePaidNonPaidData: undefined,
  facebookDemographicsData: undefined,
  facebookLocationData: undefined,
  // Instagram data
  instagramReachCurrentMonthSelection: undefined,
  instagramReachTwoMonthsAgo: undefined,
  instagramReachThreeMonthsAgo: undefined,
  instagramGenderData: undefined,
  instagramLocationData: undefined,
  instagramPosts: [],
  instagramPostsCurrentlySelectedMonth: [],
  // Twitter
  twitterData: undefined,
};

export default createReducer(initialState, {
  SET_SOCIAL_REPORTS_GRAPH_LOAD_STATE: (state, payload) => {
    return {
      ...state,
      ...payload,
    };
  },
  TRIGGER_SOCIAL_GRAPHS_FETCH: (state, payload) => {
    return {
      ...state,
      triggerFetch: payload,
    };
  },
  SET_CRM_REPORT_DATES: (state, payload) => {
    return {
      ...state,
      crmDates: payload,
    };
  },
  // Facebook *************************
  FETCHING_FACEBOOK_PAGE_LIKE_COUNT_SUCCESS: (state, payload) => {
    return {
      ...state,
      facebookPageLikeCount: (payload || {}).fan_count,
    };
  },
  FETCHING_FACEBOOK_SOURCE_AND_PAID_NON_PAID_DATA_SUCCESS: (state, payload) => {
    const { data } = payload;
    return {
      ...state,
      facebookPageSourceData: (data.filter((item) => item.name === 'page_fans_by_like_source_unique') || [])[0].values,
      facebookPagePaidNonPaidData: (data.filter((item) => item.name === 'page_fan_adds_by_paid_non_paid_unique') || [])[0].values,
    };
  },
  FETCHING_FACEBOOK_PAGE_IMPRESSIONS_SUCCESS: (state, payload) => {
    // const dailyImpressions = ((payload || []).data[0] || {}).values.filter((item) => item.isCurrentMonth);
    return {
      ...state,
      facebookPageImpressions: (payload || []).data[0].values,
    };
  },
  FETCHING_FACEBOOK_PAGE_VIDEO_VIEWS_SUCCESS: (state, payload) => {
    // const dailyVideoViews = ((payload || []).data[0] || {}).values.filter((item) => item.isCurrentMonth);
    return {
      ...state,
      facebookPageVideoViews: (payload || []).data[0].values,
    };
  },
  FETCHING_FACEBOOK_PAGE_CLICKS_SUCCESS: (state, payload) => {
    return {
      ...state,
      facebookPageClicks: payload,
    };
  },
  FETCHING_FACEBOOK_ENGAGEMENT_SUCCESS: (state, payload) => {
    const { startDate } = state.crmDates;
    const currentMonthSelection = format(addDays(new Date(startDate), 3), 'MMM');
    const monthsFiltered = ((payload || {}).data || []).filter((item) => format(new Date(item.created_time), 'MMM') === currentMonthSelection);
    return {
      ...state,
      facebookPosts: monthsFiltered,
    };
  },
  FETCHING_FACEBOOK_LIKES_AND_PAGE_VIEWS_SUCCESS: (state, payload) => {
    return {
      ...state,
      facebookLikesAndPageViews: payload.data,
    };
  },
  FETCHING_FACEBOOK_AGE_AND_GENDER_DEMOGRAPHICS_SUCCESS: (state, payload) => {
    const { data } = payload;
    const demographicData = data.find((item) => item.name === 'page_fans_gender_age');
    return {
      ...state,
      facebookDemographicsData: demographicData.values[1] ? demographicData.values[1].value : demographicData.values[0].value,
    };
  },
  FETCHING_FACEBOOK_LOCATION_DATA_SUCCESS: (state, payload) => {
    const { data } = payload;
    const locationData = data.find((item) => item.name === 'page_fans_city');
    return {
      ...state,
      facebookLocationData: locationData.values[1] ? locationData.values[1].value : locationData.values[0].value,
    };
  },
  // Instagram *************************
  FETCHING_INSTAGRAM_FOLLOW_COUNT_SUCCESS: (state, payload) => {
    return {
      ...state,
      instagramFollowerCount: payload,
    };
  },
  FETCHING_INSTAGRAM_INSIGHTS_CURRENTLY_SELECTED_MONTH_SUCCESS: (state, payload) => {
    const { instagramReach, instagramImpressions } = formatInstagramInsights({ payload });
    return {
      ...state,
      instagramCurrentMonthLoading: false,
      instagramReachCurrentMonthSelection: instagramReach,
      instagramImpressionsCurrentMonthSelection: instagramImpressions,
      ...mapToCamelCase(payload),
    };
  },
  FETCHING_INSTAGRAM_INSIGHTS_TWO_MONTHS_FROM_DATE_SELECTION_SUCCESS: (state, payload) => {
    const { instagramReach, instagramImpressions } = formatInstagramInsights({ payload });
    return {
      ...state,
      instagramReachTwoMonthsAgo: instagramReach,
      instagramImpressionsTwoMonthsAgo: instagramImpressions,
    };
  },
  FETCHING_INSTAGRAM_INSIGHTS_THREE_MONTHS_FROM_DATE_SELECTION_SUCCESS: (state, payload) => {
    const { instagramReach, instagramImpressions } = formatInstagramInsights({ payload });
    return {
      ...state,
      instagramReachThreeMonthsAgo: instagramReach,
      instagramImpressionsThreeMonthsAgo: instagramImpressions,
    };
  },
  FETCHING_INSTAGRAM_DEMOGRAPHICS_SUCCESS: (state, payload) => {
    const { data } = payload;
    const genderData = (data.find((item) => item.name === 'audience_gender_age') || {}).values;
    const locationData = (data.find((item) => item.name === 'audience_city') || {}).values;
    return {
      ...state,
      instagramGenderData: (genderData || [{}])[0].value ? (genderData || [{}])[0].value : null,
      instagramLocationData: (locationData || [{}])[0].value ? (locationData || [{}])[0].value : null,
    };
  },
  FETCHING_INSTAGRAM_POSTS_SUCCESS: (state, payload) => {
    return {
      ...state,
      instagramPosts: payload,
    };
  },
  // Twitter *************************
  FETCHING_TWITTER_DATA_SUCCESS: (state, payload) => {
    return {
      ...state,
      twitterData: payload,
    };
  },
});
